import React, { useState, useEffect } from 'react';
import { getFamilyMembers } from '../../api/userApi';
import { User } from '../../types';
import authService from '../../services/auth';
import { ProfileModal } from '../profile/ProfileModal';
import { useAppSelector } from '../../hooks/redux';

interface BirthdayEvent {
  id: string;
  name: string;
  date: Date;
  displayName: string;
  photoURL?: string;
  user: User;
}

interface BirthdayCalendarProps {
  onOpenChat?: (member: User) => void;
}

export const BirthdayCalendar: React.FC<BirthdayCalendarProps> = ({ onOpenChat }) => {
  const { loginUser: user } = useAppSelector((state) => state.user);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailedUser, setDetailedUser] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Get current month and year
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Get first day of month and number of days
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    loadFamilyMembers();
  }, []);

  const loadFamilyMembers = async () => {
    try {
      setLoading(true);
      const response = await getFamilyMembers();
      if (response.data?.success && response.data.data) {
        // Filter members who have birthdays and have made them visible
        const membersWithBirthdays = response.data.data.members.filter((member: User) =>
          member.birthday && (member.isBirthdayVisible || member.is_birthday_visible)
        );
        setFamilyMembers(membersWithBirthdays);
      }
    } catch (error) {
      console.error('Error loading family members:', error);
      setError('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  const getBirthdaysForDay = (day: number): BirthdayEvent[] => {
    return familyMembers
      .filter(member => {
        if (!member.birthday) return false;
        // Parse birthday manually to avoid timezone issues
        const birthdayParts = member.birthday.split('-');
        const birthdayMonth = parseInt(birthdayParts[1]) - 1; // Month is 0-indexed
        const birthdayDay = parseInt(birthdayParts[2]);
        return birthdayMonth === currentMonth && birthdayDay === day;
      })
      .map(member => {
        // Create date object without timezone conversion
        const birthdayParts = member.birthday!.split('-');
        const year = parseInt(birthdayParts[0]);
        const month = parseInt(birthdayParts[1]) - 1; // Month is 0-indexed
        const dayOfMonth = parseInt(birthdayParts[2]);

        return {
          id: member.id,
          name: member.displayName || member.display_name || 'Unknown',
          date: new Date(year, month, dayOfMonth),
          displayName: member.displayName || member.display_name || 'Unknown',
          photoURL: member.photoURL || member.photo_url,
          user: member
        };
      });
  };

  const handleBirthdayClick = async (birthday: BirthdayEvent) => {
    try {
      const profileData = await authService.getUserProfile(birthday.user.id);
      setDetailedUser(profileData);
      setSelectedUser(birthday.user);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setDetailedUser(birthday.user);
      setSelectedUser(birthday.user);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setDetailedUser(null);
  };

  const handleUserUpdate = (updatedUser: User) => {
    setDetailedUser(updatedUser);
  };

  const getAge = (birthday: string): number => {
    const today = new Date();
    // Parse birthday manually to avoid timezone issues
    const birthdayParts = birthday.split('-');
    const birthYear = parseInt(birthdayParts[0]);
    const birthMonth = parseInt(birthdayParts[1]) - 1; // Month is 0-indexed
    const birthDay = parseInt(birthdayParts[2]);

    let age = today.getFullYear() - birthYear;
    const monthDiff = today.getMonth() - birthMonth;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDay)) {
      age--;
    }
    return age;
  };

  const isToday = (day: number): boolean => {
    const today = new Date();
    return today.getFullYear() === currentYear &&
           today.getMonth() === currentMonth &&
           today.getDate() === day;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days array
  const calendarDays = [];

  // Add empty cells for days before the first day of month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  if (loading) {
    return (
      <div className="p-3">
        <div className="paper p-3 text-center">
          <span className="material-icons rotating mb-2" style={{ fontSize: '2rem' }}>refresh</span>
          <p>Loading birthday calendar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="paper p-3">
          <div className="alert alert-error">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Get all birthdays for the current month sorted by day
  const monthBirthdays = familyMembers
    .filter(member => {
      if (!member.birthday) return false;
      const birthdayParts = member.birthday.split('-');
      const birthdayMonth = parseInt(birthdayParts[1]) - 1;
      return birthdayMonth === currentMonth;
    })
    .map(member => {
      const birthdayParts = member.birthday!.split('-');
      const year = parseInt(birthdayParts[0]);
      const month = parseInt(birthdayParts[1]) - 1;
      const day = parseInt(birthdayParts[2]);
      return {
        id: member.id,
        name: member.displayName || member.display_name || 'Unknown',
        date: new Date(year, month, day),
        day: day,
        displayName: member.displayName || member.display_name || 'Unknown',
        photoURL: member.photoURL || member.photo_url,
        user: member
      };
    })
    .sort((a, b) => a.day - b.day);

  return (
    <div className="p-3">
      <div className="paper p-3" style={{ backgroundColor: 'var(--secondary-color)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
        {/* Calendar Header */}
        <div className="flex flex-align-center flex-justify-space-between mb-3">
          <button
            className="btn btn-text icon-btn"
            onClick={() => navigateMonth('prev')}
            title="Previous month"
          >
            <span className="material-icons">chevron_left</span>
          </button>

          <div className="text-center">
            <h2 className="h4 mb-0">{monthNames[currentMonth]} {currentYear}</h2>
            <button
              className="btn btn-text btn-sm mt-1"
              onClick={goToToday}
            >
              Today
            </button>
          </div>

          <button
            className="btn btn-text icon-btn"
            onClick={() => navigateMonth('next')}
            title="Next month"
          >
            <span className="material-icons">chevron_right</span>
          </button>
        </div>

        {/* Mobile List View */}
        <div className="birthday-list-mobile">
          {monthBirthdays.length > 0 ? (
            monthBirthdays.map(birthday => (
              <div
                key={birthday.id}
                className="birthday-list-item"
                onClick={() => handleBirthdayClick(birthday)}
              >
                {birthday.photoURL ? (
                  <img src={birthday.photoURL} alt={birthday.displayName} className="birthday-list-avatar" />
                ) : (
                  <div className="birthday-list-avatar-placeholder">
                    <span className="material-icons">cake</span>
                  </div>
                )}
                <div className="birthday-list-info">
                  <div className="birthday-list-name">{birthday.displayName}</div>
                  <div className="birthday-list-date">
                    {monthNames[currentMonth]} {birthday.day}, {currentYear}
                  </div>
                  <div className="birthday-list-age">
                    Turning {getAge(birthday.date.toISOString().split('T')[0])} years
                  </div>
                </div>
                <span className="material-icons birthday-list-icon">chevron_right</span>
              </div>
            ))
          ) : (
            <div className="alert alert-info">
              No birthdays this month.
            </div>
          )}
        </div>

        {/* Calendar Grid - Desktop only */}
        <div className="birthday-calendar birthday-calendar-desktop">
          {/* Day headers */}
          <div className="calendar-header">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-day-header">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="calendar-grid">
            {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={index} className="calendar-day empty"></div>;
              }

              const birthdays = getBirthdaysForDay(day);
              const isCurrentDay = isToday(day);

              return (
                <div
                  key={day}
                  className={`calendar-day ${isCurrentDay ? 'today' : ''} ${birthdays.length > 0 ? 'has-birthday' : ''}`}
                >
                  <div className="day-number">{day}</div>
                  {birthdays.map(birthday => (
                    <div
                      key={birthday.id}
                      className="birthday-event"
                      onClick={() => handleBirthdayClick(birthday)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="birthday-info">
                        {birthday.photoURL && (
                          <div className="birthday-avatar">
                            <img src={birthday.photoURL} alt={birthday.displayName} />
                          </div>
                        )}
                        <div className="birthday-details">
                          <div className="birthday-name">{birthday.displayName}</div>
                          <div className="birthday-age">
                            {getAge(birthday.date.toISOString().split('T')[0])} years
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <div className="flex flex-align-center flex-wrap gap-2">
            <div className="flex flex-align-center">
              <div className="calendar-legend-item today"></div>
              <span className="text-sm ml-1">Today</span>
            </div>
            <div className="flex flex-align-center">
              <div className="calendar-legend-item has-birthday"></div>
              <span className="text-sm ml-1">Birthday</span>
            </div>
          </div>

          {familyMembers.length === 0 && (
            <div className="alert alert-info mt-2">
              No family members have made their birthdays visible on the calendar yet.
            </div>
          )}
        </div>
      </div>

      <ProfileModal
        open={modalOpen}
        onClose={handleCloseModal}
        user={detailedUser}
        currentUser={user}
        onUpdate={handleUserUpdate}
        onOpenChat={onOpenChat}
      />
    </div>
  );
};